import { type HiddenTime, hiddenMsThrough, newHiddenTime, now, readStoredState, recordHidden, writeStoredState } from "./dwellClock";
import { emitPifFormEvent, emitPifSectionEvent } from "./monitoring";

/**
 * Timing for the patient intake form: how long a provider spends on it, which review section
 * costs that time, and how the form ends.
 *
 * WHY THIS IS INVISIBLE WITHOUT IT. The PIF is `?patient_intake=true` on /patients/:patientId
 * (PatientDetail/index.tsx), a query-param state rather than a route, so useScreenTimeTracking -
 * whose effect depends only on location.pathname - never sees it open or close. Same reason the
 * end-visit screens needed endVisitTiming, and this deliberately mirrors its shape.
 *
 * WHY MODULE SCOPE. The form clock has to outlive the component. Removing the param unmounts the
 * PIF, and that unmount IS the measurement's end - a component that has to survive its own
 * teardown to report cannot be the owner. The Router-level hook closes the visit; the component
 * only feeds it.
 *
 * WHY SECTION TIME IS PUSHED IN RATHER THAN PULLED OUT. usePifSectionDwell accumulates against
 * this module as it goes, instead of holding a local total and reporting at unmount. If the flush
 * on unmount is ever missed or reordered, the cost is the final section's tail rather than every
 * section of the visit.
 */

const VISIT_STORAGE_KEY = "nl.pifVisit";

/**
 * How the provider got here. `?from=appointments` is set by the appointments dashboard
 * (PatientIntakeForm reads it as isAppointmentContext); anything else reached the form from the
 * patient record itself. Worth splitting because the appointments path is the pre-visit workflow
 * and the patient-detail path is usually mid-visit, and there is no reason to expect them to take
 * the same time.
 */
export type PifEntry = "appointments" | "patient_detail";

/**
 * completed / saved is the "Mark Completed" switch, not two code paths - see notePifOutcome.
 *
 * save_failed exists because without it a failed save is indistinguishable from a successful one:
 * the mutation's onError handler shows a toast and writes to console, so the form looks saved in
 * every aggregate. It is the outcome most worth having a number for and the one nobody would
 * think to add later.
 */
export type PifOutcome = "completed" | "saved" | "abandoned" | "save_failed";

/**
 * The nine section ids as rendered, which are the keys of PatientIntakeForm's `sectionRefs` memo
 * and the values that reach Sentry as `step_name`.
 *
 * `care-gap` is filtered out of the form for users without the care-gap flag or the view_caregap
 * permission, so eight series is as correct as nine. Documented here because a missing series on
 * a chart otherwise looks like broken instrumentation.
 *
 * Not enforced at runtime: an id that drifts out of this list still gets emitted rather than
 * silently dropped, because a renamed section should show up as an unexpected series - which
 * someone notices - rather than as absent data, which nobody does.
 */
export const PIF_SECTION_IDS = [
  "care-gap",
  "reason-for-visit",
  "vitals",
  "medications-review",
  "allergy-review",
  "order-review",
  "pharmacy-review",
  "insurance-review",
  "consent-review",
] as const;

interface VisitState {
  startedAt: number;
  entry: PifEntry;
  patientId?: string;
  // Set by the component when it learns the form's fate. The visit stays open afterwards: saving
  // does not close the form, and a provider commonly saves and keeps working, so the last outcome
  // reported before the form closes is the one that describes the visit.
  outcome?: PifOutcome;
  // Accumulated active ms per section id, keyed as above. Persisted with the visit so a mid-form
  // reload does not reset the breakdown while keeping the total.
  sectionMs: Record<string, number>;
  hidden: HiddenTime;
}

let visit: VisitState | null = null;

/**
 * The section-dwell hook's "commit whatever is open right now" callback.
 *
 * Registered rather than driven by a listener of the hook's own, because both would otherwise
 * race on pagehide: window listeners fire in registration order and the Router-level hook
 * registers first, so a tab closed on the form would emit the visit before the open section had
 * been banked - losing the final section's time every single time, which is exactly the section a
 * provider was working on when they gave up.
 */
let sectionFlush: (() => void) | null = null;

export function setPifSectionFlush(fn: (() => void) | null): void {
  sectionFlush = fn;
}

function readStoredVisit(): VisitState | null {
  return readStoredState<VisitState>(VISIT_STORAGE_KEY, (parsed) => typeof (parsed as VisitState | null)?.startedAt === "number");
}

function persist(): void {
  writeStoredState(VISIT_STORAGE_KEY, visit);
}

function currentVisit(): VisitState | null {
  if (!visit) {
    visit = readStoredVisit();
    if (visit) {
      // A restored record's hidden accumulator survives JSON, but an open hidden stretch was
      // stamped in the previous page's clock domain. Reseed rather than trust it.
      visit.hidden = newHiddenTime();
      // A record written by an older build may predate this field. The validator only vouches for
      // startedAt, so anything the arithmetic below indexes into has to be re-established here or
      // a restored visit throws on the first section it tries to bank.
      if (!visit.sectionMs || typeof visit.sectionMs !== "object") visit.sectionMs = {};
    }
  }
  return visit;
}

/**
 * t0. Called when `?patient_intake` appears. Idempotent for the same patient so a re-render or an
 * unrelated query-param change does not restart the clock, but a different patient starts a new
 * visit: two forms opened back to back are two measurements, and letting the first one's t0 stand
 * would report the pair as one very long form.
 */
export function beginPifVisit(ctx: { entry: PifEntry; patientId?: string } = { entry: "patient_detail" }): void {
  const existing = currentVisit();
  if (existing && existing.patientId === ctx.patientId) return;
  if (existing) endPifVisit();

  visit = {
    startedAt: now(),
    entry: ctx.entry,
    patientId: ctx.patientId,
    sectionMs: {},
    hidden: newHiddenTime(),
  };
  persist();
}

/**
 * Records how the form ended, from the component that knows.
 *
 * The distinction the websocket cannot make: pif_completion_status_updated fires for edits made
 * by anyone, including another user on another machine, so an outcome taken from it would be
 * attributed to whichever form happened to be open. The local truth is handleSubmit plus the
 * "Mark Completed" switch.
 *
 * Last call wins, deliberately. A provider who saves, edits again and saves again has one visit
 * whose outcome is the second save, and a save that fails after one that succeeded is the outcome
 * that matters.
 */
export function notePifOutcome(outcome: PifOutcome): void {
  const state = currentVisit();
  if (!state) return;
  state.outcome = outcome;
  visit = state;
  persist();
}

/**
 * Adds active dwell time to one section. Called by usePifSectionDwell on every change of dominant
 * section, so module state always holds the running total.
 */
export function addPifSectionTime(sectionId: string, ms: number): void {
  if (!(ms > 0)) return;
  const state = currentVisit();
  if (!state) return;
  state.sectionMs[sectionId] = (state.sectionMs[sectionId] ?? 0) + ms;
  visit = state;
  persist();
}

/** Called on visibilitychange so hidden time can be subtracted from the active figure. */
export function recordPifVisibilityChange(hidden: boolean): void {
  if (!visit) return;
  recordHidden(visit.hidden, hidden);
}

export function hasOpenPifVisit(): boolean {
  return currentVisit() !== null;
}

/**
 * Terminal. Emits the form measurement plus one sample per section that accumulated time.
 *
 * No minimum-dwell floor on the section samples. Scrolling the form top to bottom makes every
 * section briefly dominant, and dropping the short ones here would tidy the distribution at the
 * cost of data that cannot be recovered - and would break the reconciliation that makes the
 * breakdown trustworthy, since sum(section active ms) should track the form's active_ms. Filter
 * in the Sentry query, where it is reversible.
 */
export function endPifVisit(): void {
  try {
    sectionFlush?.();
  } catch {
    // A broken flush costs one section's tail, not the whole visit.
  }

  const state = currentVisit();
  visit = null;
  writeStoredState(VISIT_STORAGE_KEY, null);
  if (!state) return;

  const at = now();
  const durationMs = at - state.startedAt;
  // A visit that opened and closed in the same tick is a remount or a redirect passing through,
  // not a provider filling in a form. Reporting it would drag every percentile down.
  if (durationMs <= 0) return;

  const activeMs = Math.max(0, durationMs - hiddenMsThrough(state.hidden, at));
  // No outcome reported means nothing terminal happened before the form closed.
  const outcome = state.outcome ?? "abandoned";

  emitPifFormEvent({
    outcome,
    entry: state.entry,
    durationMs,
    activeMs,
    patientId: state.patientId,
  });

  for (const [sectionId, ms] of Object.entries(state.sectionMs)) {
    if (!(ms > 0)) continue;
    emitPifSectionEvent({
      section: sectionId,
      durationMs: ms,
      outcome,
      patientId: state.patientId,
    });
  }
}

/** Test seam only. */
export function __resetPifTimingForTests(): void {
  visit = null;
  sectionFlush = null;
  writeStoredState(VISIT_STORAGE_KEY, null);
}
